import { createFormHook, useStore } from '@tanstack/react-form';
import { Button } from './button';
import type React from 'react';
import { lazy, useEffect, useRef } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { fieldContext, formContext, useFormContext } from './form/shared';
import { FormReadOnlyProvider } from './form/form-read-only';
import { FormOptionalLabelProvider } from './form/form-optional-label';
import { cn } from '../lib/utils';
import { Spinner } from './spinner';
export type { OtherImageItem } from './form/multi-image-upload-field';

import TextField from './form/text-field';
import TextareaField from './form/textarea-field';
import NumberField from './form/number-field';
import SelectTextField from './form/select-text-field';
import SelectNumberField from './form/select-number-field';
import CountryField from './form/country-field';
import RegionField from './form/region-field';
import ProvinceField from './form/province-field';
import ComboboxTextField from './form/combobox-text-field';
import SwitchField from './form/switch-field';
import CheckboxField from './form/checkbox-field';
import VideoUploadField from './form/video-upload-field';
import ImageUploadField from './form/image-upload-field';
import DocumentUploadField from './form/document-upload-field';
import MultiImageUploadField from './form/multi-image-upload-field';
import SdgField from './form/sdg-field';
import LanguagesPickerField from './form/languages-picker-field';
import CurrencyField from './form/currency-field';
import DateField from './form/date-field';
import MultiComboboxTextField from './form/multi-combobox-text-field';
import SuffixTextField from './form/suffix-text-field';
import DateTimeField from './form/datetime-field';
import ColorField from './form/color-field';
import FileField from './form/file-field';
import ImageField from './form/image-field';
import MultiCountryField from './form/multi-country-field';
import ComboboxNumberField from './form/combobox-number-field';
import MultiComboboxNumberField from './form/multi-combobox-number-field';
import PasswordField from './form/password-field';
import AltTextField from './form/alt-text-field';
import LinkTextField from './form/link-text-field';

const EditorField = lazy(() => import('./form/editor-field'));

export const MAX_SIZE = 4 * 1024 * 1024;

export const downloadFile = (blob: Blob, filename: string) => {
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export const fileToBase64 = async (file: File | undefined): Promise<string | undefined> =>
	file instanceof File
		? new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.readAsDataURL(file);
				const handleLoad = () => resolve(reader.result as string);
				reader.addEventListener('load', handleLoad);
				reader.addEventListener('error', reject);
			})
		: undefined;

export const downloadCsvTemplate = (headers: string[], fileName: string) => {
	const csv = headers.join(',');
	// Send charset utf-8 as a header in the download, is the only way to make utf-8 work on excel
	// See https://stackoverflow.com/a/18925211/6261347
	const blob = new Blob(['\ufeff', csv]);
	const downloadLink = document.createElement('a');
	const url = URL.createObjectURL(blob);
	downloadLink.href = url;
	downloadLink.download = `${fileName}.csv`;
	document.body.appendChild(downloadLink);
	downloadLink.click();
	document.body.removeChild(downloadLink);
};

export const getIdOrGenerate = (id: string | undefined): string => (!id || id === '' ? crypto.randomUUID() : id);

function SubmitButton({ label, ...props }: React.ComponentProps<typeof Button> & { label: string }) {
	const form = useFormContext();
	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => (
				<Button {...props} disabled={props.disabled ?? isSubmitting}>
					{isSubmitting && <Spinner />}
					{label}
				</Button>
			)}
		</form.Subscribe>
	);
}

function SaveDiscardButtons(props: {
	className?: string;
	unsavedChangesText?: string;
	discardText?: string;
	saveText?: string;
}) {
	const form = useFormContext();
	const { isSubmitting, isDefaultValue } = useStore(form.store, (state) => ({
		isDefaultValue: state.isDefaultValue,
		isSubmitting: state.isSubmitting,
	}));

	const unsavedText = props.unsavedChangesText ?? 'You have unsaved changes, are you sure you want to leave?';

	useBlocker({
		shouldBlockFn: () => {
			if (isDefaultValue) return false;

			const shouldLeave = confirm(unsavedText);
			return !shouldLeave;
		},
		enableBeforeUnload: !isDefaultValue,
	});

	if (isDefaultValue) return null;

	return (
		<div className={cn('flex gap-2', props.className)}>
			<Button
				type="reset"
				variant="secondary"
				onClick={(event) => {
					event.preventDefault();
					form.reset();
					// TODO: We need to double reset to remove all translated campaign texts with the translate feature,
					// is totally a bug on tanstack/form
					setTimeout(() => form.reset());
				}}
			>
				{props.discardText ?? 'Discard'}
			</Button>
			<Button disabled={isSubmitting}>
				{isSubmitting && <Spinner />}
				{props.saveText ?? 'Save'}
			</Button>
		</div>
	);
}

export { FormReadOnlyProvider, FormOptionalLabelProvider };

const { useAppForm: _useAppForm, withForm } = createFormHook({
	fieldComponents: {
		TextField,
		TextareaField,
		NumberField,
		CurrencyField,
		DateField,
		DateTimeField,
		ColorField,
		FileField,
		ImageField,
		SelectTextField,
		SelectNumberField,
		CountryField,
		MultiCountryField,
		RegionField,
		ProvinceField,
		ComboboxTextField,
		ComboboxNumberField,
		MultiComboboxTextField,
		MultiComboboxNumberField,
		CheckboxField,
		SwitchField,
		PasswordField,
		SuffixTextField,
		EditorField,
		ImageUploadField,
		DocumentUploadField,
		VideoUploadField,
		MultiImageUploadField,
		AltTextField,
		LinkTextField,
		SdgField,
		LanguagesPickerField,
	},
	formComponents: {
		SubmitButton,
		SaveDiscardButtons,
	},
	fieldContext,
	formContext,
});

function useAppFormWithScrollToError(...args: Parameters<typeof _useAppForm>): ReturnType<typeof _useAppForm> {
	const form = _useAppForm(...args);

	const submissionAttempts = useStore(form.store, (state: { submissionAttempts: number }) => state.submissionAttempts);
	const prevAttempts = useRef(0);
	useEffect(() => {
		if (submissionAttempts > prevAttempts.current) {
			prevAttempts.current = submissionAttempts;
			setTimeout(() => {
				const firstError = document.querySelector<HTMLElement>('[aria-invalid="true"]');
				if (firstError) {
					firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
					firstError.focus({ preventScroll: true });
				}
			}, 0);
		}
	}, [submissionAttempts]);

	return form;
}

export const useAppForm = useAppFormWithScrollToError as typeof _useAppForm;
export { withForm };
